import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { JackpotConfigRequest } from 'apis/adminServices/jackpotServices';
import { AdminModal, AirlinePicker } from 'components/admin';
import { format } from 'date-fns';

interface JackpotConfiguration {
  jackpotName: string;
  flights: number[];
  creditsForAnsweringRequest: number;
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

interface AddJackpotModalProps {
  newJackpot: JackpotConfiguration;
  setNewJackpot: (value: JackpotConfiguration) => void;
  onAdd: (config: JackpotConfigRequest) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

const AddJackpotModal: React.FC<AddJackpotModalProps> = ({ newJackpot, setNewJackpot, onAdd, onClose, isSubmitting }) => {
  const [error, setError] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<string>('');

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (creditError) {
      setError('Please fix validation errors before submitting');
      return;
    }

    if (!newJackpot.creditsForAnsweringRequest || newJackpot.creditsForAnsweringRequest <= 0) {
      setError('Credits for answering a request is required');
      return;
    }

    if (newJackpot.creditsForAnsweringRequest > 100) {
      setError('Credit value cannot exceed 100');
      return;
    }
   
    if (!newJackpot.flights || newJackpot.flights.length === 0) {
      setError('Please select at least one flight');
      return;
    }

    const apiConfig: JackpotConfigRequest = {
      configurationName: newJackpot.jackpotName,
      airlineId: newJackpot.flights, 
      answer: newJackpot.creditsForAnsweringRequest,
      validFrom: newJackpot.validFrom + 'T00:00:00.000Z',
      validTo: newJackpot.validTo + 'T23:59:59.999Z',
      isActive: newJackpot.isActive,
    };
  
    try {
      await onAdd(apiConfig);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add jackpot configuration';
      
      setError(message);
    }
  };

  // Handle airline selection change
  const handleAirlineChange = (airlineIds: number[]) => {
    setNewJackpot({ ...newJackpot, flights: airlineIds });
  };

  return (
    <AdminModal
      isOpen={true}
      onClose={onClose}
      title="Add New Jackpot Configuration"
      size="xl"
    >
          <form onSubmit={handleSubmit}>
              {/* Configuration Name */}
        <div className="mb-4">
                <label htmlFor="config-name" className="text-xs font-light text-gray-500">
                  Configuration Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  id="config-name"
                  value={newJackpot.jackpotName}
                  onChange={(e) => setNewJackpot({ ...newJackpot, jackpotName: e.target.value })}
            className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none mt-2 border-none"
                  placeholder="Enter Name"
                  required
                />
              </div>

        {/* Flight Selection - Using AirlinePicker Component */}
        <div className="mb-4">
          <AirlinePicker
            selectedAirlineIds={newJackpot.flights || []}
            onChange={handleAirlineChange}
                  placeholder="Search airlines..."
            label="Flights"
            showChips={true}
            disabled={isSubmitting}
            isRequired={true}
          />
              </div>

              {/* Credits */}
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-2" htmlFor="creditsForAnsweringRequest">
                  Credits for Answering a Request
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="number"
                  id="creditsForAnsweringRequest"
                  value={newJackpot.creditsForAnsweringRequest === 0 ? "" : newJackpot.creditsForAnsweringRequest}
                  placeholder="0"
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    if (value > 100) {
                      setCreditError('Credit value cannot exceed 100');
                      return;
                    }
                    setCreditError('');
                    setNewJackpot({
                      ...newJackpot,
                      creditsForAnsweringRequest: value,
                    });
                  }}
                  className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                  min="0"
                  max="100"
                  required
                />
                {creditError && <p className="text-red-500 text-xs mt-1">{creditError}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-2" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  value={String(newJackpot.isActive)}
                  onChange={(e) =>
                    setNewJackpot({
                      ...newJackpot,
                      isActive: e.target.value === 'true',
                    })
                  }
                  className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                  disabled={isSubmitting}
                >
                  <option value="true">Active</option>
                  <option value="false">Disable</option>
                </select>
              </div>

              {/* Date Pickers */}
              <div className="mb-4 flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-2" htmlFor="validFrom">Valid From
                  <span className="text-red-500 ml-1">*</span>
                  </label>
                  <DatePicker
                    id="validFrom"
                    selected={newJackpot.validFrom ? new Date(newJackpot.validFrom) : null}
                    onChange={(date: Date | null) => {
                      setNewJackpot({
                        ...newJackpot,
                        validFrom: date ? format(date, 'yyyy-MM-dd') : '',
                      });
                      // Clear error when date changes
                      setError(null);
                    }}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    placeholderText="Select start date"
                    onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-2" htmlFor="validTo">Valid To
                  <span className="text-red-500 ml-1">*</span>
                  </label>
                  <DatePicker
                    id="validTo"
                    selected={newJackpot.validTo ? new Date(newJackpot.validTo) : null}
                    onChange={(date: Date | null) => {
                      const validFromDate = newJackpot.validFrom ? new Date(newJackpot.validFrom) : null;
                      if (date && validFromDate && date < validFromDate) {
                        setError('Valid To must be after Valid From');
                        return;
                      }
                      setError(null);
                      setNewJackpot({
                        ...newJackpot,
                        validTo: date ? format(date, 'yyyy-MM-dd') : '',
                      });
                    }}
                    dateFormat="yyyy-MM-dd"
                    minDate={newJackpot.validFrom ? new Date(newJackpot.validFrom) : new Date(new Date().setHours(0, 0, 0, 0))}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    placeholderText="Select end date"
                    onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                    required
                  />
                </div>
              </div>

              {error && <div className="mb-4 text-red-500 text-xs">{error}</div>}

        <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-gray-600" disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#FFBC00] text-[#F5F9FE] text-xs rounded-lg" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
            </div>
          </form>
    </AdminModal>
  );
};

export default AddJackpotModal;
